import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight, ShieldCheck, Eye, EyeOff, Icon } from '../../components/ui/Icon';

const API_URL = import.meta.env.VITE_API_URL as string;
import { authApi } from '../../api/auth.api';
import { useAuthStore } from '../../stores/auth.store';
import Spinner from '../../components/ui/Spinner';
import { AppleSignInButton } from '../../components/auth/AppleSignInButton';
import { useTranslation } from 'react-i18next';
import { GromlyWordmark } from '../../components/brand/GromlyMark';

export default function LoginPage() {
  const [email, setEmail]                   = useState('');
  const [password, setPassword]             = useState('');
  const [error, setError]                   = useState('');
  const [loading, setLoading]               = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null);
  const [otpCode, setOtpCode]               = useState('');
  const [remember, setRemember]             = useState(true);
  const [showPassword, setShowPassword]     = useState(false);
  const [passwordless, setPasswordless]     = useState(false);
  const { setAuth }                         = useAuthStore();
  const navigate                            = useNavigate();
  const location                            = useLocation();
  const redirect                            = new URLSearchParams(location.search).get('redirect');
  const from                                = (redirect && redirect.startsWith('/')) ? redirect : ((location.state as { from?: string })?.from ?? '/dashboard');
  const registerHref                        = redirect ? `/register?redirect=${encodeURIComponent(redirect)}` : '/register';
  // Extract invitation token from redirect URL so it can piggyback on the Google/Apple OAuth flow
  const invitationToken                     = (() => {
    if (!redirect?.startsWith('/invite/accept')) return null;
    const qs = redirect.split('?')[1];
    return qs ? new URLSearchParams(qs).get('token') : null;
  })();
  const { t }                               = useTranslation();
  // ADR-0009: cuando ya sabemos qué correo quiere el usuario, se lo pasamos a
  // Google como pista para que el selector señale esa cuenta. El selector lo
  // pone el servidor y no depende de esto.
  const emailHint                           = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? email.trim() : '';
  const googleHref                          = (() => {
    const params = new URLSearchParams();
    if (invitationToken) params.set('invitationToken', invitationToken);
    if (emailHint)       params.set('loginHint', emailHint);
    const qs = params.toString();
    return `${API_URL}/auth/google${qs ? `?${qs}` : ''}`;
  })();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setPasswordless(false);
    setLoading(true);
    try {
      const result = await authApi.login({ email, password });
      if (result.requiresTwoFactor && result.twoFactorToken) {
        setTwoFactorToken(result.twoFactorToken);
      } else {
        setAuth(result.user!, result.accessToken!);
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      const res  = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
      const code = res?.data?.error;
      if (code === 'ACCOUNT_USES_GOOGLE' || code === 'ACCOUNT_USES_APPLE') {
        setPasswordless(true);
        setError(t(code === 'ACCOUNT_USES_APPLE' ? 'auth.login.appleAccount' : 'auth.login.googleAccount'));
      } else if (res?.status === 429) {
        setError(t('auth.login.tooManyAttempts'));
      } else {
        setError(t('auth.login.errorDefault'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify2FA(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { accessToken, user } = await authApi.verifyTwoFactor({ twoFactorToken: twoFactorToken!, code: otpCode });
      setAuth(user, accessToken);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('auth.twoFactor.invalidCode'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">

      {/* ── LEFT: editorial ink panel ─────────────────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between px-14 py-11 relative overflow-hidden"
        style={{ background: '#0b1420', color: '#faf7f0' }}
      >
        {/* Dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(rgba(217,179,130,0.08) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 30% 40%, #000 30%, transparent 70%)',
        }} />
        {/* Glow */}
        <div className="absolute -right-24 -bottom-16 w-96 h-96 rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle at center, rgba(217,179,130,0.18), transparent 60%)',
          filter: 'blur(30px)',
        }} />

        {/* Top bar */}
        <div className="relative z-10 flex items-center justify-between">
          <GromlyWordmark size={38} dark />
          <div className="flex items-center gap-2.5 font-mono text-[10px] tracking-[1.6px] uppercase" style={{ color: 'rgba(250,247,240,0.5)' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#d9b382' }} />
            Travel Co. · est. MMXXVI
          </div>
        </div>

        {/* Hero */}
        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-7" style={{ color: '#d9b382' }}>
            <span className="w-9 h-px" style={{ background: '#d9b382', opacity: 0.6 }} />
            {t('auth.login.heroEyebrow')}
          </div>
          <h1 className="font-serif font-light italic leading-none tracking-tight mb-5" style={{ fontSize: 'clamp(52px, 6.5vw, 82px)' }}>
            <span style={{ color: '#faf7f0', fontStyle: 'normal' }}>{t('auth.login.heroLine1')}</span><br />
            <span style={{ color: '#d9b382', fontStyle: 'italic' }}>{t('auth.login.heroLine2')}</span><br />
            <span style={{ color: '#faf7f0', fontStyle: 'normal' }}>{t('auth.login.heroLine3')} </span><span style={{ color: '#d9b382', fontStyle: 'italic' }}>{t('auth.login.heroLine4')}</span>
          </h1>
          <p className="font-sans text-base leading-relaxed mb-10" style={{ color: 'rgba(250,247,240,0.7)', lineHeight: 1.75 }}>
            {t('auth.login.heroSubtitle')}
          </p>

        </div>

        {/* Ticker */}
        <div className="relative z-10 flex items-center justify-between gap-4 pt-5" style={{ borderTop: '1px solid rgba(250,247,240,0.12)' }}>
          <p className="font-mono text-[10.5px] tracking-[1.6px] uppercase whitespace-nowrap" style={{ color: 'rgba(250,247,240,0.45)' }}>
            <span style={{ color: '#d9b382', fontWeight: 500 }}>Lisboa</span>
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            Hanoi
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            Cape Town
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            Cusco
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            Kyoto
            <span className="mx-2.5" style={{ color: 'rgba(250,247,240,0.2)' }}>/</span>
            <span style={{ color: '#d9b382', fontWeight: 500 }}>Barcelona</span>
          </p>
          <span className="font-mono text-[10px] tracking-[1.4px] uppercase whitespace-nowrap" style={{ color: 'rgba(250,247,240,0.3)' }}>v2.4 · 2026</span>
        </div>
      </div>

      {/* ── RIGHT: form panel ─────────────────────────────────────────────── */}
      <div
        className="flex flex-col px-14 py-11 relative overflow-hidden"
        style={{ background: '#faf7f0' }}
      >
        {/* Subtle grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(11,20,32,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(11,20,32,0.025) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />

        {/* Top */}
        <div className="relative z-10 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[1.6px] uppercase" style={{ color: '#6b7689' }}>
            {t('auth.login.breadcrumb')}
          </span>
          <span className="text-sm" style={{ color: '#6b7689' }}>
            {t('auth.login.newHere')}{' '}
            <Link to={registerHref} className="font-sans font-medium" style={{ color: '#0b1420', borderBottom: '1px solid #0b1420', paddingBottom: 1 }}>
              {t('auth.login.createAccount')}
            </Link>
          </span>
        </div>

        {/* Form centered */}
        <div className="flex-1 flex items-center justify-center relative z-10">
          <div className="w-full max-w-sm">

            {twoFactorToken ? (
              /* ── 2FA ── */
              <>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(11,20,32,0.06)' }}>
                    <Icon as={ShieldCheck} size={20} style={{ color: '#0b1420' }} />
                  </div>
                  <div>
                    <h2 className="font-serif font-semibold text-2xl tracking-tight" style={{ color: '#0b1420' }}>{t('auth.twoFactor.title')}</h2>
                    <p className="text-sm" style={{ color: '#6b7689' }}>{t('auth.twoFactor.subtitle')}</p>
                  </div>
                </div>

                <form onSubmit={handleVerify2FA} className="space-y-4">
                  <div>
                    <label className="block font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
                      {t('auth.twoFactor.codeLabel')}
                    </label>
                    <input
                      type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                      className="w-full text-center text-2xl tracking-widest font-mono outline-none transition-all"
                      style={{ border: '1px solid #d6dae2', borderRadius: 10, height: 54, background: '#fff', color: '#0b1420', fontSize: 22 }}
                      placeholder="000000"
                      value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required autoFocus autoComplete="one-time-code"
                    />
                  </div>

                  {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">⚠ {error}</p>}

                  <button
                    type="submit"
                    disabled={loading || otpCode.length < 6}
                    className="w-full flex items-center justify-center gap-3 font-serif font-medium text-lg transition-all"
                    style={{ height: 56, background: '#0b1420', color: '#faf7f0', borderRadius: 10, border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: otpCode.length < 6 ? 0.5 : 1 }}
                  >
                    {loading ? <Spinner size="sm" /> : <>{t('auth.twoFactor.verify')}<span className="w-7 h-7 rounded-full flex items-center justify-center not-italic" style={{ background: '#d9b382', color: '#0b1420' }}><Icon as={ArrowRight} size={13} /></span></>}
                  </button>
                  <button type="button" onClick={() => { setTwoFactorToken(null); setOtpCode(''); setError(''); }}
                    className="w-full text-sm text-center py-1 transition-colors"
                    style={{ color: '#6b7689', background: 'none', border: 'none', cursor: 'pointer' }}>
                    {t('auth.twoFactor.back')}
                  </button>
                </form>
              </>
            ) : (
              /* ── Login ── */
              <>
                {/* Form head */}
                <div className="mb-8">
                  <div className="flex items-center gap-2.5 font-mono text-[10.5px] tracking-[2px] uppercase mb-4" style={{ color: '#8a5a2b' }}>
                    <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[10px]" style={{ borderColor: '#d9b382', color: '#8a5a2b' }}>01</span>
                    {t('auth.login.title')}
                  </div>
                  <h2 className="font-sans font-light leading-none tracking-tight mb-3" style={{ color: '#0b1420', fontSize: 42, letterSpacing: -0.5 }}>
                    {t('auth.login.continueJourney')}
                  </h2>
                  <p className="font-sans font-medium leading-relaxed" style={{ color: '#3a4558', fontSize: 15, lineHeight: 1.65 }}>
                    {t('auth.login.continueSubtitle')}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="flex items-center justify-between font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
                      {t('auth.login.email')}
                    </label>
                    <div className="flex items-center gap-2.5 transition-all" style={{ border: '1px solid #d6dae2', borderRadius: 10, height: 54, background: '#fff', padding: '0 14px' }}
                      onFocusCapture={e => (e.currentTarget.style.borderColor = '#0b1420', e.currentTarget.style.boxShadow = '0 0 0 3px rgba(11,20,32,0.06)')}
                      onBlurCapture={e => (e.currentTarget.style.borderColor = '#d6dae2', e.currentTarget.style.boxShadow = 'none')}
                    >
                      <Icon as={Mail} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
                      <input
                        id="email" type="email"
                        className="flex-1 outline-none bg-transparent text-sm"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#0b1420', height: '100%', border: 'none' }}
                        placeholder="you@example.com"
                        value={email} onChange={e => setEmail(e.target.value)}
                        required autoComplete="email"
                      />
                      <span className="font-mono text-[9.5px] tracking-[1.6px] uppercase px-1.5 py-0.5 rounded" style={{ color: '#6b7689', border: '1px solid #d6dae2', background: '#f4f1ea', letterSpacing: '1.6px' }}>@</span>
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="pw" className="flex items-center justify-between font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
                      {t('auth.login.password')}
                      <Link to="/forgot-password" className="font-mono text-[10px] tracking-[1.6px] uppercase transition-colors" style={{ color: '#0b1420', textDecoration: 'none' }}>
                        {t('auth.login.forgot')}
                      </Link>
                    </label>
                    <div className="flex items-center gap-2.5 transition-all" style={{ border: '1px solid #d6dae2', borderRadius: 10, height: 54, background: '#fff', padding: '0 14px' }}
                      onFocusCapture={e => (e.currentTarget.style.borderColor = '#0b1420', e.currentTarget.style.boxShadow = '0 0 0 3px rgba(11,20,32,0.06)')}
                      onBlurCapture={e => (e.currentTarget.style.borderColor = '#d6dae2', e.currentTarget.style.boxShadow = 'none')}
                    >
                      <Icon as={Lock} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
                      <input
                        id="pw" type={showPassword ? 'text' : 'password'}
                        className="flex-1 outline-none bg-transparent text-sm"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#0b1420', height: '100%', border: 'none' }}
                        placeholder={t('auth.login.passwordPlaceholder')}
                        value={password} onChange={e => setPassword(e.target.value)}
                        required autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(s => !s)}
                        className="shrink-0 p-1.5 rounded hover:bg-slate-100 transition-colors cursor-pointer"
                        style={{ color: '#6b7689' }}
                        aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                        tabIndex={-1}
                      >
                        <Icon as={showPassword ? EyeOff : Eye} size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Remember me */}
                  <div className="flex items-center gap-2.5 text-sm" style={{ color: '#3a4558' }}>
                    <button type="button" onClick={() => setRemember(r => !r)}
                      className="flex items-center justify-center transition-colors"
                      style={{ width: 18, height: 18, border: `1.4px solid ${remember ? '#0b1420' : '#d6dae2'}`, borderRadius: 4, background: remember ? '#0b1420' : '#fff', flexShrink: 0, cursor: 'pointer' }}>
                      {remember && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#faf7f0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="5 12 10 17 20 7" />
                        </svg>
                      )}
                    </button>
                    {t('auth.login.rememberMe')}
                  </div>

                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                      <p>⚠ {error}</p>
                      {/* ADR-0009: el error de cuenta sin contraseña lleva salida, no sólo diagnóstico. */}
                      {passwordless && (
                        <Link
                          to={`/forgot-password${emailHint ? `?email=${encodeURIComponent(emailHint)}` : ''}`}
                          className="inline-block mt-1.5 font-medium"
                          style={{ color: '#0b1420', textDecoration: 'underline' }}
                        >
                          {t('auth.login.setPassword')}
                        </Link>
                      )}
                    </div>
                  )}

                  {/* CTA */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 font-sans font-medium text-lg transition-all"
                    style={{ marginTop: 10, height: 56, background: '#0b1420', color: '#faf7f0', borderRadius: 10, border: 'none', cursor: loading ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = '#000'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 10px 24px -10px rgba(11,20,32,0.45)'; }}}
                    onMouseLeave={e => { e.currentTarget.style.background = '#0b1420'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                  >
                    {loading ? <Spinner size="sm" /> : (
                      <>
                        {t('auth.login.submit')}
                        <span className="w-7 h-7 rounded-full flex items-center justify-center not-italic" style={{ background: '#d9b382', color: '#0b1420' }}>
                          <Icon as={ArrowRight} size={13} />
                        </span>
                      </>
                    )}
                  </button>
                </form>

                {/* OR divider */}
                <div className="flex items-center gap-3.5 my-5 font-mono text-[10px] tracking-[2px] uppercase" style={{ color: '#6b7689' }}>
                  <span className="flex-1 h-px" style={{ background: '#d6dae2' }} />
                  {t('auth.login.orContinueWith')}
                  <span className="flex-1 h-px" style={{ background: '#d6dae2' }} />
                </div>

                {/* Social */}
                <div className="flex flex-col gap-2.5">
                  <AppleSignInButton
                    invitationToken={invitationToken}
                    onClick={() => { if (redirect) sessionStorage.setItem('auth-redirect', redirect); }}
                  />
                  <a
                    href={googleHref}
                    onClick={() => { if (redirect) sessionStorage.setItem('auth-redirect', redirect); }}
                    className="flex items-center justify-center gap-2.5 text-sm font-medium transition-all no-underline"
                    style={{ height: 48, background: '#fff', border: '1px solid #d6dae2', borderRadius: 10, color: '#0b1420', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#0b1420'; e.currentTarget.style.background = '#f4f1ea'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#d6dae2'; e.currentTarget.style.background = '#fff'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
                    {t('auth.login.continueWithGoogle')}
                  </a>
                </div>

              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center justify-between font-mono text-[10px] tracking-[1.4px] uppercase whitespace-nowrap gap-3" style={{ color: '#6b7689' }}>
          <div className="flex items-center gap-1">
            <Link to="/privacy" className="transition-colors" style={{ color: '#3a4558', textDecoration: 'none' }}>{t('auth.login.footer.privacy')}</Link>
            <span style={{ opacity: 0.4 }}> · </span>
            <Link to="/terms" className="transition-colors" style={{ color: '#3a4558', textDecoration: 'none' }}>{t('auth.login.footer.terms')}</Link>
            <span style={{ opacity: 0.4 }}> · </span>
            <a href="#" className="transition-colors" style={{ color: '#3a4558', textDecoration: 'none' }}>{t('auth.login.footer.status')}</a>
          </div>
          <div>{t('auth.login.footer.copyright')}</div>
        </div>
      </div>
    </div>
  );
}
